import React, { useState, useEffect } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { SecondaryButton } from '../../components/Buttons/Buttons';
import { useVersionInfo } from '../../SettingsProvider/VersionInfoContext';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import './AboutSettingsPage.css';
import { NativePlatform } from '../../Contracts';
import { CitrixWorkspaceIcon } from '../../Icons/CitrixWorkspaceIcon/CitrixWorkspaceIcon';
import { WORKSPACE_UPDATE_SETTING_IDS } from '../../config/SettingIds';
import { getSettingConfig, RadioSettingConfig } from '../../config/SettingsDefinitions';

interface AboutSettingsPageProps {
	title: string;
	onNavigatingBack: () => void;
}

export const AboutSettingsPage: React.FC<AboutSettingsPageProps> = ({
	title,
	onNavigatingBack,
}) => {
	const { versionInfo } = useVersionInfo();
	const versionValue = versionInfo.version + ' (' + versionInfo.release + ')';
	const [isDetailsCopied, setIsDetailsCopied] = useState(false);
	const [updateChannel, setUpdateChannel] = useState('');

	// Get platform from bridge
	const bridge = SettingsNativeBridgeFactory.getBridge();
	const platform = bridge.getPlatform();

	// Map platform to display name
	const platformDisplayName =
		platform === NativePlatform.MAC
			? 'macOS'
			: platform === NativePlatform.WINDOWS
				? 'Windows'
				: platform === NativePlatform.LINUX
					? 'Linux'
					: platform;

	const operatingSystem = `Citrix Workspace app for ${platformDisplayName}`;

	useEffect(() => {
		const fetchUpdateChannel = async () => {
			try {
				const response = await bridge.getSetting({
					settingKey: WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
				});
				if (response.status === 0 && response.data?.settingValue) {
					const channelValue = String(response.data.settingValue);
					const channelConfig = getSettingConfig(
						WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL
					) as RadioSettingConfig;
					const matchedOption = channelConfig?.options?.find(
						(opt) => opt.value === channelValue
					);
					setUpdateChannel(matchedOption?.label ?? channelValue);
				}
			} catch {
				// Leave updateChannel empty if fetch fails
			}
		};
		fetchUpdateChannel();
	}, []);

	const handleCopyAppDetails = () => {
		const operatingSystemValue = operatingSystem;
		const versionValue = versionInfo.version + '(' + versionInfo.release + ')';
		const updateChannelValue = updateChannel;

		const appDetails = `Operating system: ${operatingSystemValue}\nVersion: ${versionValue}\nUpdate channel: ${updateChannelValue}`;
		navigator.clipboard.writeText(appDetails);
		setIsDetailsCopied(true);
		setTimeout(() => setIsDetailsCopied(false), 2000);
	};

	return (
		<SettingsPage
			title={title}
			canNavigateBack={!!onNavigatingBack}
			onNavigatingBack={onNavigatingBack}
			settings={
				<div>
					<SettingCard
						settingId="about-content"
						title={
							<div className="section-title-container">
								<CitrixWorkspaceIcon width={20} height={20} />
								<h3 className="section-title">Citrix Workspace app specifications</h3>
							</div>
						}
						description=""
						content={
							<div className="app-specs-section">
								<div className="about-specifications">
									<div className="spec-item">
										<span className="spec-label">Operating system</span>
										<span className="spec-value">{operatingSystem}</span>
									</div>
									<div className="spec-item">
										<span className="spec-label">Version</span>
										<span className="spec-value">{versionValue}</span>
									</div>
									<div className="spec-item">
										<span className="spec-label">Update channel</span>
										<span className="spec-value">{updateChannel}</span>
									</div>
								</div>
								<SecondaryButton
									className="copy-details-button"
									onClick={handleCopyAppDetails}
								>
									{isDetailsCopied ? 'Copied' : 'Copy details'}
								</SecondaryButton>
							</div>
						}
					/>
					<div className="about-footer">
						<a
							className="link-button"
							href={versionInfo.thirdPartyNoticesUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							Citrix Third party notices
						</a>
						<span className="copyright">
							© {versionInfo.copyrightYear} Citrix Systems, Inc. All Rights Reserved.
						</span>
						<a
							className="link-button"
							href="https://www.citrix.com"
							target="_blank"
							rel="noopener noreferrer"
						>
							https://www.citrix.com
						</a>
					</div>
				</div>
			}
		/>
	);
};
